import { resolveProfile, type ProfileUser } from '@/lib/profileIdentity';
// CVG-WEB-1 (L96 option 1) — seat predicate for the admin Discount Approvals
// queue. A PENDING request whose approverTarget is 'manager' and which has NOT
// been escalated is "seated with the team head": the chain expects the team
// head to decide it. Admin acting on such a row is a chain override and must
// be explicitly confirmed. Rows seated with admin (approverTarget='admin_tier';
// legacy pre-2026-08-25 rows say 'karan') or
// already escalated (escalatedToRole set) are the admin's to decide — no gate.
export interface DiscountSeatFields {
  status: string;
  approverTarget?: string | null;
  escalatedToRole?: string | null;
  /** SS list annotation: requestedPct <= THIS viewer's cap (their slab 1). */
  withinBracket?: boolean;
}

export function isSeatedWithTeamHead(req: DiscountSeatFields): boolean {
  return req.status === 'pending' && req.approverTarget === 'manager' && !req.escalatedToRole;
}

export const TEAM_HEAD_CHIP_LABEL = 'With team head — awaiting their action';

export const TEAM_HEAD_OVERRIDE_CONFIRM =
  'This request is with the team head. Override the chain and decide it now?';

// 🔒 2026-10-03 — the override confirm is for an ADMIN/OWNER acting on a row
// seated with a team head. The team head deciding their own seat is the chain
// working, not an override; they were being asked to "override" themselves.
export function needsChainOverrideConfirm(req: DiscountSeatFields, viewerRoleName: string | null | undefined): boolean {
  const role = (viewerRoleName ?? '').toLowerCase().trim();
  return MD_APPROVER_ROLES.has(role) && isSeatedWithTeamHead(req);
}

// ─── CVG2-DQ (L98) — admin/owner pending-queue toggle ────────────────────────
// Segments the PENDING list by seat: rows the admin must act on now
// ("Waiting for your approval": admin-tier-seated, escalated, or legacy null-seat)
// vs rows the chain has parked with a team head ("With managers").
export type PendingSeatSegment = 'waiting' | 'with_managers';

export const PENDING_SEGMENT_LABELS: Record<PendingSeatSegment, string> = {
  waiting: 'Waiting for your approval',
  with_managers: 'With managers',
};

export function splitPendingBySeat<T extends DiscountSeatFields>(
  rows: T[],
): { waiting: T[]; withManagers: T[] } {
  const waiting: T[] = [];
  const withManagers: T[] = [];
  for (const row of rows) {
    (isSeatedWithTeamHead(row) ? withManagers : waiting).push(row);
  }
  return { waiting, withManagers };
}

// ─── CVG2-DQ (L98) — decided-row role suffix from approvedByRole ─────────────
// Maps the stamped literal role → display label: "(admin)" for Layer A,
// "(team head)" for the B4(a) team-head literals (see dealScope.ts). Legacy
// rows (null approvedByRole) return null → caller omits the suffix. Unknown
// non-null roles humanize (underscores → spaces) rather than mislabel.
const DECIDED_ADMIN_ROLES = new Set(['admin', 'owner', 'super_admin', 'ceo']);
const DECIDED_TEAM_HEAD_ROLES = new Set(['manager', 'sr_sales_manager', 'manager_sales']);

export function decidedByRoleLabel(role: string | null | undefined): string | null {
  const r = (role ?? '').toLowerCase().trim();
  if (!r) return null;
  if (DECIDED_ADMIN_ROLES.has(r)) return 'admin';
  if (DECIDED_TEAM_HEAD_ROLES.has(r)) return 'team head';
  return r.replace(/_/g, ' ');
}

// ─── CVG3-DISC (L103-1b) — universal seat chip on pending rows ───────────────
// Every viewer (manager / coordinator / requester) sees where a pending request
// is seated: 'manager' + not escalated → "With manager («team-head name»)",
// 'admin_tier' (legacy 'karan') or escalated → "With MD". The team-head name is read from
// `teamHeadName` when the serializer carries it (fail-soft: the current list
// payload does NOT — role-only copy "With manager" renders until it does, see
// C-SEAT cross-module dep). Decided rows and rows without seat fields → null.
export interface SeatChipFields extends DiscountSeatFields {
  teamHeadName?: string | null;
}

export const MD_SEAT_CHIP_LABEL = 'With MD';

export function seatChipLabel(req: SeatChipFields): string | null {
  if (req.status !== 'pending') return null;
  const target = (req.approverTarget ?? '').toLowerCase().trim();
  const escalated = (req.escalatedToRole ?? '').trim().length > 0;
  // 'admin_tier' is the current persisted value; 'karan' survives on rows
  // written before the 2026-08-25 rename until the data migration runs.
  if (target === 'admin_tier' || target === 'karan' || escalated) return MD_SEAT_CHIP_LABEL;
  if (target === 'manager') {
    const name = (req.teamHeadName ?? '').trim();
    return name ? `With manager (${name})` : 'With manager';
  }
  return null; // seat fields absent → no chip (fail-soft per C-SEAT)
}

// ─── CVG3-DISC (L103-1a) — coordinator read/track gate ───────────────────────
// Coordinators (raise + status view) must never see live Approve/Reject.
// Every other role keeps today's behavior — the backend approve/reject
// authorization remains the real guard; this only hides dead/forbidden UI.
// SUN-40 D7 (2026-10-18): CRM operations (P04, role operations_manager) is
// read-only too — profile-access.ts gives P04 GET only on /discount-requests.
const READ_ONLY_DISCOUNT_ROLES = new Set(['coordinator', 'sales_coordinator', 'operations_manager']);

// SUN-40 D7 — the same rule for a whole viewer: role-based read-only, rep-only,
// AND profile P04 (CRM operations). P04 can also come from the job title when the
// user has no role (resolveProfile), and the server profile gate is GET-only for
// P04 on /discount-requests (profile-access.ts), so Approve/Reject would 403.
export function canDecideDiscountViewer(user: ProfileUser | null | undefined): boolean {
  const role = user?.role?.name ?? '';
  return canDecideDiscountRole(role) && !isRepOnlyDiscountRole(role) && resolveProfile(user) !== 'P04';
}

// SUN-40 — mirrors REP_ONLY_DISCOUNT_ROLES in discount-requests.routes.ts: the
// server scopes these roles to their OWN requests and never lets them decide.
export const REP_ONLY_DISCOUNT_ROLES: ReadonlySet<string> = new Set([
  'field_executive', 'sales_rep', 'sales_executive', 'sales_exec', 'coordinator', 'assistant_executive_sales',
]);

export function isRepOnlyDiscountRole(role: string | null | undefined): boolean {
  return REP_ONLY_DISCOUNT_ROLES.has((role ?? '').toLowerCase().trim());
}

export function canDecideDiscountRole(role: string | null | undefined): boolean {
  return !READ_ONLY_DISCOUNT_ROLES.has((role ?? '').toLowerCase().trim());
}

// ─── ESC-WEB (2026-08-31, Manak) — Escalate on the WEB card ──────────────────
// 🔒 WHY THIS EXISTS. Until today the web Discount Approvals card offered only
// Approve and Reject. `POST /discount-requests/:id/escalate` has existed all
// along and the MOBILE inbox has always had the button
// (approvals_screen.dart:540) — the web simply never got one. A team head on a
// desktop who could not decide a request had to Approve it, Reject it, or leave
// it. Passing it up the chain was phone-only.
//
// 🔒 The role list MIRRORS the backend gate in
// discount-requests.routes.ts:1738 exactly. A role outside it gets a 403, so
// showing the button to them would be dead UI — the same reasoning as
// canDecideDiscountRole above. The backend remains the real guard.
// SUN-40 D7: super_admin + ceo are in the server's BASE_APPROVER_ROLES too.
const ESCALATE_ROLES = new Set([
  'owner', 'admin', 'super_admin', 'ceo', 'manager', 'sr_sales_manager', 'manager_sales',
]);

export function canEscalateDiscountRole(role: string | null | undefined): boolean {
  return ESCALATE_ROLES.has((role ?? '').toLowerCase().trim());
}

/**
 * Is this request ALREADY with the MD?
 *
 * 🔒 `decideEscalate` (approver-ceiling.ts:78) is a NOOP once approverTarget is
 * the admin tier — the server returns `{ noop: true }` and changes nothing.
 * Rendering an Escalate button there would promise an action that does nothing,
 * so it is hidden instead. 'karan' is the pre-2026-08-25 persisted value and
 * survives on older rows.
 */
export function isAlreadyWithMD(req: DiscountSeatFields): boolean {
  const target = (req.approverTarget ?? '').toLowerCase().trim();
  return target === 'admin_tier' || target === 'karan'
    || (req.escalatedToRole ?? '').trim().length > 0;
}

/** Show the Escalate affordance? Pending + allowed role + not already at the top. */
export function canShowEscalate(
  req: DiscountSeatFields,
  role: string | null | undefined,
): boolean {
  return req.status === 'pending'
    && canEscalateDiscountRole(role)
    && !isAlreadyWithMD(req);
}

// ─── 2026-09-15 (Dixit, PROP-90477) — Approve mirrors the server's H5 gate ────
// 🔒 discount-requests.routes.ts (approve route, H5): when approverTarget is the
// admin tier ('admin_tier', legacy 'karan') only LAYER_A_ROLES may approve —
// approver-ceiling.ts LAYER_A_ROLES = admin, owner, super_admin, ceo. Anyone
// else gets 403 "you can escalate it but not approve it". Offering Approve to
// them was a dead button that ended in a bare "Failed to approve request".
// The backend remains the real guard; this only hides the forbidden action.
const MD_APPROVER_ROLES = new Set(['admin', 'owner', 'super_admin', 'ceo']);

export function isAdminTierApproverTarget(target: string | null | undefined): boolean {
  const t = (target ?? '').toLowerCase().trim();
  return t === 'admin_tier' || t === 'karan';
}

/** Show a live Approve for this viewer? Pending + decide-capable + (viewer is Layer A, or not MD-owned AND inside their own slab 1). */
export function canApproveDiscountRequest(
  req: DiscountSeatFields,
  role: string | null | undefined,
): boolean {
  if (req.status !== 'pending' || !canDecideDiscountRole(role)) return false;
  if (MD_APPROVER_ROLES.has((role ?? '').toLowerCase().trim())) return true;
  if (isAdminTierApproverTarget(req.approverTarget)) return false;
  // 🔒 2026-09-15 (Manak): beyond the manager's own slab → Escalate, not Approve.
  // The server annotates withinBracket on SS lists; absent (non-SS) → unchanged.
  return req.withinBracket !== false;
}

export const ESCALATE_CONFIRM =
  'Send this request up to the MD for approval? You will no longer be able to decide it.';
